import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Erinnerung } from '../types';
import erinnerungService from '../services/erinnerungService';
import { Icon, fehlerText } from './ui';
import { markiereNeu, ankunft } from '../utils/ankunft';
import { faelligkeit, erinnerungText } from '../utils/erinnerung';
import { DatumFeld } from './DatumZeit';

const formatDate = (d: string) => {
  const [j, m, t] = d.split('-');
  return `${t}.${m}.${j}`;
};

interface Formular {
  id?: number;
  notiz: string;
  faelligAm: string;
}

interface Props {
  kundeId: number;
  /** Formular für eine neue Erinnerung offen (der Knopf dazu sitzt im Seitenkopf) */
  anlegenOffen: boolean;
  onAnlegenSchliessen: () => void;
}

/**
 * Offene Erinnerungen eines Kunden, ganz oben auf der Kundenseite, damit sie beim Öffnen auffallen.
 * Ohne Erinnerungen und ohne offenes Formular erscheint nichts.
 */
const KundenErinnerungen: React.FC<Props> = ({ kundeId, anlegenOffen, onAnlegenSchliessen }) => {
  const [erinnerungen, setErinnerungen] = useState<Erinnerung[]>([]);
  const [form, setForm] = useState<Formular | null>(null);
  const [saving, setSaving] = useState(false);

  const laden = async () => {
    try {
      setErinnerungen(await erinnerungService.getByKunde(kundeId));
    } catch (err) {
      toast.error(fehlerText(err, 'Erinnerungen konnten nicht geladen werden'));
    }
  };

  useEffect(() => {
    laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kundeId]);

  useEffect(() => {
    if (anlegenOffen) setForm({ notiz: '', faelligAm: '' });
  }, [anlegenOffen]);

  const schliessen = () => {
    setForm(null);
    onAnlegenSchliessen();
  };

  const speichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    if (!form.notiz.trim()) { toast.error('Bitte einen Text eingeben'); return; }
    setSaving(true);
    const req = { kundeId, notiz: form.notiz.trim(), faelligAm: form.faelligAm || null };
    try {
      const gespeichert = form.id
        ? await erinnerungService.update(form.id, req)
        : await erinnerungService.create(req);
      markiereNeu(`erinnerung-${gespeichert.id}`);
      toast.success(form.id ? 'Erinnerung gespeichert' : 'Erinnerung angelegt');
      schliessen();
      await laden();
    } catch (err) {
      toast.error(fehlerText(err, 'Erinnerung konnte nicht gespeichert werden'));
    } finally {
      setSaving(false);
    }
  };

  const erledigt = async (e: Erinnerung) => {
    try {
      await erinnerungService.markAsErledigt(e.id);
      setErinnerungen(prev => prev.filter(x => x.id !== e.id));
      toast.success('Erinnerung erledigt');
    } catch (err) {
      toast.error(fehlerText(err, 'Erinnerung konnte nicht als erledigt markiert werden'));
    }
  };

  const loeschen = async (e: Erinnerung) => {
    if (!window.confirm(`Erinnerung „${erinnerungText(e)}“ löschen?`)) return;
    try {
      await erinnerungService.delete(e.id);
      setErinnerungen(prev => prev.filter(x => x.id !== e.id));
      toast.success('Erinnerung gelöscht');
    } catch (err) {
      toast.error(fehlerText(err, 'Erinnerung konnte nicht gelöscht werden'));
    }
  };

  if (erinnerungen.length === 0 && !form) return null;

  const formular = form && (
    <form onSubmit={speichern} className="ui-aufklappen bg-white rounded-lg border border-gray-200 p-3 space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Erinnerung</label>
        <textarea
          value={form.notiz}
          onChange={e => setForm({ ...form, notiz: e.target.value })}
          rows={2}
          autoFocus
          placeholder="z. B. Hund im Garten, vorher klingeln"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y"
        />
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Fällig am (optional)</label>
          <DatumFeld
            value={form.faelligAm}
            onChange={(v) => setForm({ ...form, faelligAm: v })}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <p className="flex-1 min-w-[180px] text-xs text-gray-500 pb-2">Mit Datum erscheint die Erinnerung auch im Dashboard.</p>
        <div className="flex gap-2">
          <button type="button" onClick={schliessen} className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">Abbrechen</button>
          <button type="submit" disabled={saving} className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-sm hover:bg-brand-700 disabled:opacity-60">
            {form.id ? 'Speichern' : 'Erinnerung anlegen'}
          </button>
        </div>
      </div>
    </form>
  );

  return (
    <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50/60 p-3 md:p-4">
      <h2 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
        <Icon.Glocke className="h-4 w-4 text-amber-500" />
        Erinnerungen
      </h2>

      <div className="space-y-2">
        {form && !form.id && formular}
        {erinnerungen.map(e => {
          if (form?.id === e.id) return <React.Fragment key={e.id}>{formular}</React.Fragment>;
          const status = e.faelligAm ? faelligkeit(e.faelligAm) : null;
          const glow = ankunft(`erinnerung-${e.id}`);
          const vonHand = e.berichtId == null;
          return (
            <div key={e.id} ref={glow.ref} className={`rounded-lg p-3 flex items-start gap-3 ${status ? status.card : 'border-l-4 border-amber-300 bg-white'} ${glow.className}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{erinnerungText(e)}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {vonHand
                    ? (e.erstelltVon ? `von ${e.erstelltVon}` : null)
                    : e.berichtDatum ? `aus dem Bericht vom ${formatDate(e.berichtDatum)}` : 'aus einem Bericht'}
                </p>
              </div>
              {status && e.faelligAm && (
                <div className="flex-shrink-0 flex flex-col items-end gap-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.badge}`}>{status.label}</span>
                  <span className="text-xs text-gray-400">{formatDate(e.faelligAm)}</span>
                </div>
              )}
              <div className="flex-shrink-0 flex items-center">
                {vonHand && (
                  <button
                    onClick={() => setForm({ id: e.id, notiz: e.notiz ?? '', faelligAm: e.faelligAm ?? '' })}
                    title="Bearbeiten"
                    className="p-1.5 rounded-full text-gray-400 hover:text-brand-600 hover:bg-brand-50"
                  >
                    <Icon.Edit className="w-4 h-4" />
                  </button>
                )}
                {vonHand && (
                  <button
                    onClick={() => loeschen(e)}
                    title="Löschen"
                    className="p-1.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50"
                  >
                    <Icon.Trash className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => erledigt(e)}
                  title="Als erledigt markieren"
                  className="p-1.5 rounded-full text-gray-400 hover:text-green-600 hover:bg-green-50"
                >
                  <Icon.Check className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KundenErinnerungen;
